import React, { useState, useEffect } from "react"
import { Helmet } from "react-helmet"
import Layout from "../../../components/Layout"
import Header from "../../../components/Header"
import FaqAccordion from "../../../components/FaqAccordion"
import { EmailPopup } from "../../../components/reports"
import {
  hasReportAccess,
  submitEmailForReportAccess,
} from "../../../components/reports/utils"
import "../../../components/reports/styles/reportStyles.css"
import { gradientLightPink } from "../../../ds/gradients.module.css"

const ArrowRightIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
)

const TrendingUpIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="48"
    height="48"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
)

const DollarSignIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
)

const UsersIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
)

const SparklesIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
    <path d="M5 3v4" />
    <path d="M19 17v4" />
    <path d="M3 5h4" />
    <path d="M17 19h4" />
  </svg>
)

const MailIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
)

interface EventTypeConfig {
  label: string
  postingRate: number
  avgImpressions: number
  cpm: number
}

interface AudienceTypeConfig {
  label: string
  multiplier: number
  cpmAdjust: number
}

interface EMVResults {
  estimatedPosts: number
  conservativeEMV: number
  realisticEMV: number
  optimisticEMV: number
  paidAdCost: number
  paidPremium: number
  impressions: {
    conservative: number
    realistic: number
    optimistic: number
  }
}

interface FormData {
  attendees: string
  eventType: string
  targetAudience: string
  email: string
  company: string
  premagicInvestment: string
}

const eventTypes: Record<string, EventTypeConfig> = {
  "tech-conference": {
    label: "Tech Conference",
    postingRate: 0.18,
    avgImpressions: 1800,
    cpm: 45,
  },
  "trade-show": {
    label: "Trade Show",
    postingRate: 0.12,
    avgImpressions: 1500,
    cpm: 40,
  },
  "corporate-event": {
    label: "Corporate Event",
    postingRate: 0.2,
    avgImpressions: 1600,
    cpm: 42,
  },
  "industry-summit": {
    label: "Industry Summit",
    postingRate: 0.15,
    avgImpressions: 2000,
    cpm: 50,
  },
}

const audienceTypes: Record<string, AudienceTypeConfig> = {
  mixed: { label: "Mixed Audience", multiplier: 1.0, cpmAdjust: 0 },
  enterprise: { label: "Enterprise/Director+", multiplier: 1.2, cpmAdjust: 10 },
  "c-suite": { label: "C-Suite Heavy", multiplier: 1.4, cpmAdjust: 25 },
}

const formatCurrency = (num: number): string => {
  if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
  return `$${num.toLocaleString()}`
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  return num.toLocaleString()
}

const EMVCalculator = () => {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState<FormData>({
    attendees: "",
    eventType: "",
    targetAudience: "",
    email: "",
    company: "",
    premagicInvestment: "",
  })
  const [showResults, setShowResults] = useState(false)

  const REPORT_ID = "emv-calculator"
  const [showEmailPopup, setShowEmailPopup] = useState(true)
  const [hasAccess, setHasAccess] = useState(false)

  useEffect(() => {
    if (hasReportAccess(REPORT_ID)) {
      setShowEmailPopup(false)
      setHasAccess(true)
    }
  }, [])

  const handleEmailSubmit = async (email: string) => {
    const success = await submitEmailForReportAccess(REPORT_ID, email)
    if (success) {
      setShowEmailPopup(false)
      setHasAccess(true)
    } else {
      alert("There was an error processing your request. Please try again.")
    }
  }

  const calculateEMV = (): EMVResults | null => {
    const attendees = parseInt(formData.attendees) || 0
    const eventConfig = eventTypes[formData.eventType]
    const audienceConfig = audienceTypes[formData.targetAudience]

    if (!eventConfig || !audienceConfig) return null

    const estimatedPosts = Math.round(attendees * eventConfig.postingRate)
    const baseImpressions =
      estimatedPosts * eventConfig.avgImpressions * audienceConfig.multiplier
    const baseCPM = eventConfig.cpm + audienceConfig.cpmAdjust

    const conservativeImpressions = baseImpressions * 0.8
    const conservativeEMV = (conservativeImpressions / 1000) * baseCPM

    const realisticImpressions = baseImpressions
    const realisticEMV = (realisticImpressions / 1000) * baseCPM

    const optimisticImpressions = baseImpressions * 1.2
    const optimisticEMV = (optimisticImpressions / 1000) * baseCPM * 1.5

    const paidCPM = baseCPM * 2.5
    const paidAdCost = (realisticImpressions / 1000) * paidCPM

    return {
      estimatedPosts,
      conservativeEMV: Math.round(conservativeEMV),
      realisticEMV: Math.round(realisticEMV),
      optimisticEMV: Math.round(optimisticEMV),
      paidAdCost: Math.round(paidAdCost),
      paidPremium: Math.round(paidAdCost - realisticEMV),
      impressions: {
        conservative: Math.round(conservativeImpressions),
        realistic: Math.round(realisticImpressions),
        optimistic: Math.round(optimisticImpressions),
      },
    }
  }

  const results = showResults ? calculateEMV() : null

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const goToResults = () => {
    if (formData.attendees && formData.eventType && formData.targetAudience) {
      setStep(2)
      setShowResults(true)
    }
  }

  const resetCalculator = () => {
    setStep(1)
    setShowResults(false)
  }

  const canCalculate =
    formData.attendees && formData.eventType && formData.targetAudience

  const premagicInvestmentNum = parseInt(formData.premagicInvestment) || 0

  return (
    <Layout
      title="EMV Calculator: Free Earned Media Value Calculator for Events"
      description="Calculate your event's earned media value in seconds. Free EMV calculator that turns attendee shares, reach & impressions into a dollar figure you can report."
      keywords={[
        "emv calculator, earned media calculator, earned media value calculator, event EMV calculator, event ROI calculator, LinkedIn event reach, event marketing ROI, attendee content value, organic social reach events, event advertising value, LinkedIn CPM calculator, event engagement calculator",
      ]}
    >
      <Helmet>
        <meta
          property="og:title"
          content="EMV Calculator: Free Earned Media Value Calculator for Events"
        />
        <meta
          property="og:description"
          content="Calculate your event's earned media value in seconds. Free EMV calculator that turns attendee shares, reach & impressions into a dollar figure you can report."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://premagic.com/reports/emv-calculator/"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Free EMV Calculator for Events | Premagic"
        />
        <meta
          name="twitter:description"
          content="Calculate your event's earned media value in seconds — turn attendee shares, reach & impressions into a dollar figure you can report."
        />
        <style>{`
          .emv-select {
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
            background-repeat: no-repeat;
            background-position: right 16px center;
            background-size: 16px;
          }
        `}</style>
      </Helmet>

      <div className={gradientLightPink}>
        <Header />
        <div className="pm-container py-10 md:py-14 text-center">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-4"
            style={{ backgroundColor: "#f1f4ff", color: "#1F0F61" }}
          >
            <SparklesIcon />
            Free ROI Calculator
          </div>
          <h1
            className="font-bold text-2xl md:text-4xl"
            style={{ color: "var(--color-blue-dark, #1F0F61)" }}
          >
            Free EMV Calculator: Measure Your
            <br />
            Event&apos;s Earned Media Value
          </h1>
          <p className="text-gray-600 mt-4 text-center max-w-2xl mx-auto text-lg">
            Use this free EMV calculator to find your event&apos;s earned media
            value — the dollar equivalent of all the organic reach, impressions,
            and shares your event generated. Enter your numbers below to turn
            attendee advocacy into a metric you can put in front of sponsors and
            leadership.
          </p>
        </div>
      </div>

      {showEmailPopup && (
        <EmailPopup
          onSubmit={handleEmailSubmit}
          reportName="Event EMV Calculator"
        />
      )}

      <div className={`pm-container py-12 ${!hasAccess ? "blur-content" : ""}`}>
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full text-white flex items-center justify-center text-sm font-semibold"
                style={{
                  backgroundColor: step === 2 ? "#22c55e" : "#1F0F61",
                }}
              >
                {step === 2 ? "\u2713" : "1"}
              </div>
              <span
                className={`text-sm font-medium ${
                  step === 1 ? "text-gray-900" : "text-gray-500"
                }`}
              >
                Event Details
              </span>
            </div>
            <div
              className="w-12 h-0.5"
              style={{
                backgroundColor: step === 2 ? "#1F0F61" : "#d1d5db",
              }}
            />
            <div className="flex items-center gap-2">
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                style={{
                  backgroundColor: step === 2 ? "#1F0F61" : "#d1d5db",
                  color: step === 2 ? "#ffffff" : "#6b7280",
                }}
              >
                2
              </div>
              <span
                className={`text-sm font-medium ${
                  step === 2 ? "text-gray-900" : "text-gray-500"
                }`}
              >
                Your Results
              </span>
            </div>
          </div>

          {step === 1 && (
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <div className="space-y-8">
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    How many attendees do you expect?
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                      <UsersIcon />
                    </div>
                    <input
                      type="number"
                      value={formData.attendees}
                      onChange={e =>
                        handleInputChange("attendees", e.target.value)
                      }
                      placeholder="e.g., 5000"
                      className="w-full pl-12 pr-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Expected or registered attendees for your event
                  </p>
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    What type of event?
                  </label>
                  <select
                    value={formData.eventType}
                    onChange={e =>
                      handleInputChange("eventType", e.target.value)
                    }
                    className="emv-select w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none bg-white"
                  >
                    <option value="">Select event type</option>
                    {Object.entries(eventTypes).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Different event types see different engagement rates
                  </p>
                </div>

                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    What&apos;s your audience profile?
                  </label>
                  <select
                    value={formData.targetAudience}
                    onChange={e =>
                      handleInputChange("targetAudience", e.target.value)
                    }
                    className="emv-select w-full px-4 py-4 text-lg border-2 border-gray-200 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all appearance-none bg-white"
                  >
                    <option value="">Select audience type</option>
                    {Object.entries(audienceTypes).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label}
                      </option>
                    ))}
                  </select>
                  <p className="mt-2 text-sm text-gray-500">
                    Senior audiences have larger networks and higher ad value
                  </p>
                </div>

                <button
                  onClick={goToResults}
                  disabled={!canCalculate}
                  className="w-full text-white font-semibold py-4 px-8 rounded-xl text-lg transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:bg-gray-300 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: canCalculate ? "#1F0F61" : undefined,
                  }}
                >
                  Calculate Your EMV
                  <ArrowRightIcon />
                </button>
              </div>
            </div>
          )}

          {step === 2 && results && (
            <div className="space-y-6">
              <div
                className="rounded-2xl shadow-xl p-8 md:p-12 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #1F0F61 0%, #7251f4 100%)",
                }}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <p
                      className="text-sm font-medium mb-2 opacity-70"
                      style={{ color: "#c7d2fe" }}
                    >
                      ESTIMATED EARNED MEDIA VALUE
                    </p>
                    <h2
                      className="text-5xl md:text-6xl font-bold mb-2"
                      style={{ color: "#ffffff" }}
                    >
                      {formatCurrency(results.realisticEMV)}
                    </h2>
                    <p
                      className="text-lg opacity-80"
                      style={{ color: "#e0e7ff" }}
                    >
                      From {formatNumber(results.estimatedPosts)} attendee posts
                      on LinkedIn
                    </p>
                  </div>
                  <div className="hidden md:block opacity-50">
                    <TrendingUpIcon />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white border-opacity-20">
                  <div>
                    <p className="text-xs mb-1" style={{ color: "#c7d2fe" }}>
                      Conservative
                    </p>
                    <p
                      className="text-xl font-bold"
                      style={{ color: "#ffffff" }}
                    >
                      {formatCurrency(results.conservativeEMV)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: "#c7d2fe" }}>
                      Realistic
                    </p>
                    <p
                      className="text-xl font-bold"
                      style={{ color: "#ffffff" }}
                    >
                      {formatCurrency(results.realisticEMV)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: "#c7d2fe" }}>
                      Optimistic
                    </p>
                    <p
                      className="text-xl font-bold"
                      style={{ color: "#ffffff" }}
                    >
                      {formatCurrency(results.optimisticEMV)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <div className="flex items-start gap-4 mb-6">
                  <div
                    className="p-3 rounded-xl"
                    style={{ backgroundColor: "#fff7ed" }}
                  >
                    <div style={{ color: "#d97706" }}>
                      <DollarSignIcon />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      The Organic Advantage
                    </h3>
                    <p className="text-gray-600">
                      Buying equivalent reach through LinkedIn Sponsored Content
                      would cost significantly more:
                    </p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6">
                    <p className="text-sm text-green-700 font-medium mb-2">
                      Organic EMV (Your Event)
                    </p>
                    <p className="text-3xl font-bold text-green-600">
                      {formatCurrency(results.realisticEMV)}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      Value of attendee-generated content
                    </p>
                  </div>

                  <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
                    <p className="text-sm text-red-700 font-medium mb-2">
                      Paid Advertising Cost
                    </p>
                    <p className="text-3xl font-bold text-red-600">
                      {formatCurrency(results.paidAdCost)}
                    </p>
                    <p className="text-xs text-gray-600 mt-2">
                      To buy same reach on LinkedIn
                    </p>
                  </div>
                </div>

                <div
                  className="border-2 rounded-xl p-6"
                  style={{
                    backgroundColor: "#f1f4ff",
                    borderColor: "#c7d2fe",
                  }}
                >
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <p
                        className="text-sm font-medium mb-1"
                        style={{ color: "#1F0F61" }}
                      >
                        Paid Advertising Premium
                      </p>
                      <p
                        className="text-4xl font-bold"
                        style={{ color: "#1F0F61" }}
                      >
                        {formatCurrency(results.paidPremium)}
                      </p>
                      <p className="text-sm text-gray-600 mt-2">
                        The extra cost of buying this reach through ads
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        className="text-white px-4 py-2 rounded-lg font-bold text-lg"
                        style={{ backgroundColor: "#1F0F61" }}
                      >
                        {Math.round(
                          (results.paidAdCost / results.realisticEMV) * 100 -
                            100
                        )}
                        % more
                      </div>
                      <p className="text-xs text-gray-500 mt-1">expensive</p>
                    </div>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded">
                  <p className="text-sm text-gray-700">
                    <strong>What this means:</strong> Organic attendee content
                    generates real advertising value. The question is — are you
                    capturing and maximizing this value? That&apos;s where
                    Premagic helps.
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  How We Calculated This
                </h3>

                <div className="space-y-4">
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-gray-600">Expected attendees</span>
                    <span className="font-semibold text-gray-900">
                      {formatNumber(parseInt(formData.attendees))}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-gray-600">
                      Estimated posts (at{" "}
                      {(
                        eventTypes[formData.eventType].postingRate * 100
                      ).toFixed(0)}
                      % posting rate)
                    </span>
                    <span className="font-semibold text-gray-900">
                      {formatNumber(results.estimatedPosts)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-gray-600">
                      Total impressions (realistic)
                    </span>
                    <span className="font-semibold text-gray-900">
                      {formatNumber(results.impressions.realistic)}
                    </span>
                  </div>
                  <div className="flex justify-between py-3 border-b border-gray-200">
                    <span className="text-gray-600">
                      LinkedIn CPM (organic)
                    </span>
                    <span className="font-semibold text-gray-900">
                      $
                      {eventTypes[formData.eventType].cpm +
                        audienceTypes[formData.targetAudience].cpmAdjust}
                    </span>
                  </div>
                </div>

                <div
                  className="mt-6 p-4 rounded-xl"
                  style={{ backgroundColor: "#f1f4ff" }}
                >
                  <p className="text-sm text-gray-700">
                    <strong>Note:</strong> These calculations are based on
                    industry benchmarks from 2024-2026 LinkedIn advertising
                    data. Organic content from personal profiles generates 2.75x
                    more reach than company pages, and attendee posts carry an
                    authenticity premium worth 1.5-2x paid advertising.
                  </p>
                </div>
              </div>

              <div
                className="rounded-2xl shadow-xl p-8 md:p-12 text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #111827 0%, #1f2937 100%)",
                }}
              >
                <div className="max-w-xl">
                  <h3
                    className="text-3xl font-bold mb-4"
                    style={{ color: "#ffffff" }}
                  >
                    Calculate Your Premagic ROI
                  </h3>
                  <p className="mb-6" style={{ color: "#d1d5db" }}>
                    See your actual return on investment. Input your estimated
                    Premagic platform spend to calculate ROI.
                  </p>

                  <div className="space-y-4">
                    <div>
                      <label
                        className="block text-sm font-medium mb-2"
                        style={{ color: "#d1d5db" }}
                      >
                        Estimated Premagic Investment (Optional)
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg">
                          $
                        </span>
                        <input
                          type="number"
                          value={formData.premagicInvestment}
                          onChange={e =>
                            handleInputChange(
                              "premagicInvestment",
                              e.target.value
                            )
                          }
                          placeholder="e.g., 15000"
                          className="w-full pl-8 pr-4 py-3 rounded-lg text-gray-900 border-2 border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none"
                        />
                      </div>
                      <p className="text-xs mt-1" style={{ color: "#9ca3af" }}>
                        Your total event engagement platform cost
                      </p>
                    </div>

                    {premagicInvestmentNum > 0 && (
                      <div
                        className="border-2 rounded-xl p-6"
                        style={{
                          backgroundColor: "rgba(34, 197, 94, 0.15)",
                          borderColor: "#4ade80",
                        }}
                      >
                        <p
                          className="text-sm font-medium mb-2"
                          style={{ color: "#86efac" }}
                        >
                          Your Premagic ROI
                        </p>
                        <div className="flex items-baseline gap-2">
                          <p
                            className="text-5xl font-bold"
                            style={{ color: "#4ade80" }}
                          >
                            {Math.round(
                              (results.realisticEMV / premagicInvestmentNum -
                                1) *
                                100
                            )}
                            %
                          </p>
                          <span
                            className="text-lg"
                            style={{ color: "#86efac" }}
                          >
                            return
                          </span>
                        </div>
                        <p
                          className="text-sm mt-3"
                          style={{ color: "#d1d5db" }}
                        >
                          For every ${(premagicInvestmentNum / 1000).toFixed(0)}
                          K invested, you generate{" "}
                          {formatCurrency(results.realisticEMV)} in earned media
                          value
                        </p>
                        <div className="mt-4 pt-4 border-t border-green-400 border-opacity-30">
                          <div className="flex justify-between text-sm">
                            <span style={{ color: "#d1d5db" }}>
                              Investment:
                            </span>
                            <span
                              className="font-semibold"
                              style={{ color: "#ffffff" }}
                            >
                              {formatCurrency(premagicInvestmentNum)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mt-2">
                            <span style={{ color: "#d1d5db" }}>
                              EMV Generated:
                            </span>
                            <span
                              className="font-semibold"
                              style={{ color: "#ffffff" }}
                            >
                              {formatCurrency(results.realisticEMV)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm mt-2">
                            <span style={{ color: "#d1d5db" }}>
                              ROI Multiple:
                            </span>
                            <span
                              className="font-bold"
                              style={{ color: "#4ade80" }}
                            >
                              {(
                                results.realisticEMV / premagicInvestmentNum
                              ).toFixed(1)}
                              x
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* TODO: Re-enable detailed ROI report lead capture
                    <div className="border-t border-gray-700 pt-6 mt-6">
                      <p className="text-lg font-semibold mb-4" style={{ color: "#ffffff" }}>
                        Want a Detailed ROI Report?
                      </p>
                      <input type="email" value={formData.email} onChange={e => handleInputChange("email", e.target.value)} placeholder="Your email" className="w-full px-4 py-3 rounded-lg text-gray-900 border-2 border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none mb-3" />
                      <input type="text" value={formData.company} onChange={e => handleInputChange("company", e.target.value)} placeholder="Company name" className="w-full px-4 py-3 rounded-lg text-gray-900 border-2 border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none mb-3" />
                      <button className="w-full text-white font-semibold py-4 px-8 rounded-lg transition-all flex items-center justify-center gap-2" style={{ backgroundColor: "#7251f4" }}>
                        <MailIcon />
                        Get Detailed Report
                      </button>
                      <p className="text-xs text-center mt-3" style={{ color: "#9ca3af" }}>
                        See how to maximize this EMV with advocacy posters, AI photos, and sponsor attribution
                      </p>
                    </div>
                    */}
                  </div>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={resetCalculator}
                  className="font-medium inline-flex items-center gap-2 hover:underline"
                  style={{ color: "#1F0F61" }}
                >
                  &larr; Try different numbers
                </button>
              </div>
            </div>
          )}

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 mb-4">
              Trusted by event teams at
            </p>
            <div className="flex items-center justify-center gap-4 md:gap-8 text-gray-400 flex-wrap">
              <span className="font-semibold">Dubai World Trade Centre</span>
              <span className="hidden md:inline">&bull;</span>
              <span className="font-semibold">GITEX Global</span>
              <span className="hidden md:inline">&bull;</span>
              <span className="font-semibold">Event Tech Awards Winner</span>
            </div>
          </div>
        </div>
      </div>

      <FaqAccordion
        faqs={[
          {
            question: "What is EMV (earned media value)?",
            answer:
              "EMV is the estimated dollar value of media exposure you earned organically — shares, mentions, impressions — rather than paid for. It lets you quantify word-of-mouth and advocacy.",
          },
          {
            question: "How is earned media value calculated?",
            answer:
              "EMV multiplies your earned impressions or engagements by a cost-per-equivalent rate (what that reach would have cost via paid media). This calculator does it automatically.",
          },
          {
            question: "Why does EMV matter for events?",
            answer:
              "It turns “the event felt buzzy” into a number you can report to sponsors and executives, proving ROI beyond ticket sales.",
          },
        ]}
      />
    </Layout>
  )
}

export default EMVCalculator
